import { Tooltip } from "@/base";
import { useFormControllerContext } from "@/contexts";
import { useFocusWithin } from "@/hooks";
import { getCalendarDate } from "@/utilities";
import {
    cn,
    DateInput as NextUiDateInput,
    type DateInputProps as NextUiDateInputProps,
} from "@heroui/react";
import { type DateValue, CalendarDate } from "@internationalized/date";
import { formatDate } from "@sr-partner-portal/common";
import {
    type ReactNode,
    type Ref,
    useCallback,
    useMemo,
    useState,
} from "react";
import {
    type FieldValues,
    type UseControllerProps,
    useController as useFieldController,
} from "react-hook-form";

interface DateInputProps<T extends FieldValues> {
    label: string;
    fieldName: UseControllerProps<T>["name"];
    isRequired?: boolean;
    isDisabled?: boolean;
    isViewOnly?: boolean;
    isFullWidth?: boolean;
    copyFormat?: string;
    className?: string;
    size?: NextUiDateInputProps["size"];
    hideLabel?: boolean;
    clearOnEmpty?: boolean;
    startContent?: ReactNode;
    endContent?: ReactNode;
    minValue?: DateValue;
    maxValue?: DateValue;
}

export const DateInput = <T extends FieldValues>({
    label,
    fieldName,
    isRequired,
    isDisabled,
    isViewOnly = false,
    isFullWidth = false,
    copyFormat = "dd.LL.yyyy",
    className,
    size,
    hideLabel,
    clearOnEmpty,
    startContent,
    endContent,
    minValue,
    maxValue,
}: DateInputProps<T>) => {
    const [isCopyTooltipOpen, setIsCopyTooltipOpen] = useState(false);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const formController = useFormControllerContext();
    const { ref, isFocusWithin } = useFocusWithin();

    const fieldController = useFieldController<T>({
        control: formController.control,
        name: fieldName,
    });

    const date =
        (fieldController.field.value as unknown) instanceof Date
            ? (fieldController.field.value as Date)
            : null;

    const calenderDate = getCalendarDate(date);

    const handleCopy = useCallback(async () => {
        try {
            const formattedValue = formatDate(
                calenderDate.toDate(tz),
                copyFormat,
            );

            await navigator.clipboard.writeText(formattedValue);
            setIsCopyTooltipOpen(true);
            setTimeout(() => setIsCopyTooltipOpen(false), 1000);
        } catch (err) {
            console.error("Failed to copy text: ", err);
        }
    }, [calenderDate, copyFormat, tz]);

    const isClear = useMemo(() => {
        if (!clearOnEmpty) {
            return false;
        }

        return !(isFocusWithin || calenderDate);
    }, [isFocusWithin, calenderDate, clearOnEmpty]);

    return (
        <div className={cn("relative", isFullWidth && "w-full", className)}>
            <NextUiDateInput
                ref={ref as Ref<HTMLDivElement>}
                label={hideLabel ? undefined : label}
                aria-label={label}
                // @ts-ignore: Looks like some issue in nextui
                value={calenderDate}
                onChange={(v) => {
                    // @ts-ignore:see above
                    fieldController.field.onChange(v ? v.toDate(tz) : null);
                }}
                className={cn(isViewOnly && "opacity-70")}
                classNames={{
                    base: cn(
                        "[&_*[data-slot=segment][data-type=literal]]:px-0",
                        !calenderDate &&
                            isClear &&
                            "[&_*[data-slot=segment]]:opacity-0",
                    ),
                    label: "text-sm",
                }}
                isDisabled={isDisabled || isViewOnly}
                isInvalid={!!fieldController.fieldState.error}
                errorMessage={fieldController.fieldState.error?.message}
                isRequired={isRequired}
                fullWidth={isFullWidth}
                size={size}
                startContent={startContent}
                endContent={endContent}
                minValue={minValue}
                maxValue={maxValue}
                /**
                 * We pass a random date in a month with 31 days due to a bug in HeroUI date input
                 * https://github.com/heroui-inc/heroui/issues/5272
                 */
                placeholderValue={new CalendarDate(2025, 1, 1)}
            />
            {calenderDate && isViewOnly && (
                <Tooltip
                    content="Kopiert"
                    placement="top-end"
                    closeDelay={2000}
                    isOpen={isCopyTooltipOpen}
                >
                    <button
                        type="button"
                        className="absolute top-0 left-0 h-full w-full"
                        onClick={handleCopy}
                    />
                </Tooltip>
            )}
        </div>
    );
};
